import { Button, Modal, WriteError } from '@shared/ui';

import { useArchiveRelease } from '../hooks/use-release-components';

import { explainReleaseWriteFailure } from './release-error-copy';

import type { ReleaseComponent } from '../schemas/release-component';

/**
 * Confirm archiving. Mounted only while open. Archiving removes the release from
 * the list and from its own page; it changes no flag and withdraws no override.
 * Refused by the API while an operation holds the release.
 */
export function ArchiveReleaseDialog({
    release,
    onClose,
    onArchived,
}: {
    release: ReleaseComponent;
    onClose: () => void;
    onArchived: () => void;
}) {
    const archive = useArchiveRelease(release.id);
    const failure = archive.error ? explainReleaseWriteFailure(archive.error) : null;
    const { rowVersion } = release;

    const submit = () => {
        if (rowVersion === undefined) return;
        archive.mutate({ expectedRowVersion: rowVersion }, { onSuccess: onArchived });
    };

    return (
        <Modal
            open
            onClose={onClose}
            dismissable={!archive.isPending}
            title={`Archive ${release.name}?`}
            description="The release leaves the components list and its page stops opening. No flag changes and no override is withdrawn."
            footer={
                <>
                    <Button variant="ghost" onClick={onClose} disabled={archive.isPending}>
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={submit}
                        disabled={rowVersion === undefined || archive.isPending}
                    >
                        {archive.isPending ? 'Archiving…' : 'Archive release'}
                    </Button>
                </>
            }
        >
            {failure ? (
                <div
                    role="alert"
                    className="rounded-lg border border-danger-line bg-danger-surface px-3.5 py-2.5 text-[12.5px] leading-relaxed text-danger-text"
                >
                    <p className="font-semibold">{failure.title}</p>
                    <p className="mt-0.5 text-danger-muted">{failure.body}</p>
                    {failure.detail !== undefined && (
                        <p className="mt-1.5 text-danger-muted">{failure.detail}</p>
                    )}
                </div>
            ) : (
                archive.error && <WriteError error={archive.error} />
            )}
        </Modal>
    );
}
